import { useTranslation } from 'react-i18next'
import React, { ComponentProps } from 'react'
import { cn } from '../utils'
import { Ort, Standort, Wegpunkt } from '../models'

interface BfnStandortBoxProps extends ComponentProps<'div'> {
  standort: Standort | null
  'data-testid': string
  className?: string
}

export default function BfnStandortBox({
  standort,
  className,
  'data-testid': testId,
  children,
  ...props
}: Readonly<React.PropsWithChildren<BfnStandortBoxProps>>) {
  const { t } = useTranslation('ui-components')

  return (
    <div
      className={cn('flex flex-col w-full', className)}
      {...props}
      data-testid={`${testId}-standortbox-div`}
    >
      <div className="text-xs" data-testid={`${testId}-standortbox-label`}>
        {t('standort_box.label')}
      </div>
      <div
        className="p-1 border rounded border-gray-300 dark:border-gray-700"
        data-testid={`${testId}-div`}
      >
        {!standort && (
          <p data-testid={`${testId}-empty-state`}>
            {t('standort_box.empty_state')}
          </p>
        )}
        {standort && (
          <div
            className="grid grid-cols-2 lg:grid-cols-2 gap-1"
            data-testid={`${testId}-standort-wrapper`}
          >
            <div className="flex flex-col">
              <span
                className="text-sm"
                data-testid={`${testId}-wegpunkt-label`}
              >
                {t('standort_box.fields.wegpunkt')}
              </span>
              <span className="font-bold" data-testid={`${testId}-wegpunkt`}>
                {t(`standort.wegpunkt.${standort.wegpunkt}`)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm" data-testid={`${testId}-ort-label`}>
                {t('standort_box.fields.ort')}
              </span>
              <span className="font-bold" data-testid={`${testId}-ort`}>
                {t(`standort.ort.${standort.ort}`)}
              </span>
            </div>
            {standort.wegpunkt === Wegpunkt.Kilometrierung && (
              <div className="flex flex-col">
                <span
                  className="text-sm"
                  data-testid={`${testId}-kilometrierung-label`}
                >
                  {t('standort_box.fields.kilometrierung')}
                </span>
                <span
                  className="font-bold"
                  data-testid={`${testId}-kilometrierung`}
                >
                  {standort.kilometrierung}
                </span>
              </div>
            )}
            {standort.wegpunkt === Wegpunkt.Signal && (
              <div className="flex flex-col">
                <span
                  className="text-sm"
                  data-testid={`${testId}-signal-label`}
                >
                  {t('standort_box.fields.signal')}
                </span>
                <span className="font-bold" data-testid={`${testId}-signal`}>
                  {standort.signal}
                </span>
              </div>
            )}
            {standort.wegpunkt === Wegpunkt.Weiche && (
              <div className="flex flex-col">
                <span
                  className="text-sm"
                  data-testid={`${testId}-weiche-label`}
                >
                  {t('standort_box.fields.weiche')}
                </span>
                <span className="font-bold" data-testid={`${testId}-weiche`}>
                  {standort.weiche}
                </span>
              </div>
            )}
            {standort.ort === Ort.Strecke && (
              <>
                <div className="flex flex-col">
                  <span
                    className="text-sm"
                    data-testid={`${testId}-strecke-von-label`}
                  >
                    {t('standort_box.fields.von')}
                  </span>
                  <span
                    className="font-bold"
                    data-testid={`${testId}-strecke-von`}
                  >
                    {standort.von}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span
                    className="text-sm"
                    data-testid={`${testId}-strecke-nach-label`}
                  >
                    {t('standort_box.fields.nach')}
                  </span>
                  <span
                    className="font-bold"
                    data-testid={`${testId}-strecke-nach`}
                  >
                    {standort.nach}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span
                    className="text-sm"
                    data-testid={`${testId}-gleisart-label`}
                  >
                    {t('standort_box.fields.gleisart')}
                  </span>
                  <span
                    className="font-bold"
                    data-testid={`${testId}-gleisart`}
                  >
                    {t(`standort.gleisart.${standort.gleisart}`)}
                  </span>
                </div>
              </>
            )}
            {standort.ort === Ort.Bahnhof && (
              <>
                <div className="flex flex-col">
                  <span
                    className="text-sm"
                    data-testid={`${testId}-bahnhof-gleis-label`}
                  >
                    {t('standort_box.fields.gleis')}
                  </span>
                  <span
                    className="font-bold"
                    data-testid={`${testId}-bahnhof-gleis`}
                  >
                    {standort.gleis}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span
                    className="text-sm"
                    data-testid={`${testId}-bahnhof-betriebsstelle-label`}
                  >
                    {t('standort_box.fields.betriebsstelle')}
                  </span>
                  <span
                    className="font-bold"
                    data-testid={`${testId}-bahnhof-betriebsstelle`}
                  >
                    {standort.betriebsstelle}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
        {children && <div className="mt-1.5">{children}</div>}
      </div>
    </div>
  )
}
